import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Divider,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { AgriculturePolicyDescription } from "../agriculturePolicies/AgriculturePolicyDescription";

import { AgricultureClaimDescription } from "./AgricultureClaimDescription";
import { AgriculturePolicyClaimsList } from "./AgriculturePolicyClaimsList";
import EnvironmentCondition from "../claim/EnvironmentCondition";
import { HazardProvider } from "../riskAssessment/HazardMapContext";
import AgriculturePolicyDetailsMap from "../map/AgriculturePolicyDetailsMap";
import DopplerVmiDbzMap from "../claim/DopplerVmiDbzMap";
import DopplerDbzMap from "../claim/DopplerDbzMap";
import { ParcelsList } from "../agriculturePolicies/ParcelsList";

import { NdviByParcel } from "./NdviByParcel";
import { computeDopplerFrequencyAndData } from "../util/computeDopplerFrequencyAndData";

import { useTranslation } from "react-i18next";
import httpClient from "../../lib/httpClient";
import { saveUserMonitorData } from "../util/userUtils";
import { apiError } from "../../lib/apiError";

export const AgricultureClaimShow = (props) => {
  const policyNumber = props.match.params.policyId.toString();
  const complainId = props.match.params.complainId.toString();

  const { t } = useTranslation();

  const [policy, setPolicy] = useState(null);
  const [policyClaims, setPolicyClaims] = useState([]);
  const [claim, setClaim] = useState(null);
  const [isPolicyFetching, setIsPolicyFetching] = useState(false);

  const [longitude, setLongitude] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [currentlyVisiblePartitas, setCurrentlyVisiblePartitas] = useState({});

  const [showDbzMap, setShowDbzMap] = useState(false);
  const [showVmiDbzMap, setShowVmiDbzMap] = useState(false);
  const [dateOfEvent, setDateOfEvent] = useState(null);
  const [dopplerData, setDopplerData] = useState(null);
  const [dopplerRequestDetails, setDopplerRequestDetails] = useState(null);

  const updateShowDbz = (checked) => setShowDbzMap(checked);
  const updateShowVmiDbz = (checked) => setShowVmiDbzMap(checked);

  useEffect(() => {
    setIsPolicyFetching(true);
    saveUserMonitorData("agriClaimShow", policyNumber + "/" + complainId);

    const getClaimData = async () => {
      try {
        const claimResult = await httpClient().get(`/agricultureClaims/${policyNumber}/${complainId}`);

        const { policy, claim } = claimResult.data;
        const currPolicy = policy;

        setClaim(claim);
        setPolicy(currPolicy);

        const foundLatitude = currPolicy?.location[1];
        const foundLongitude = currPolicy?.location[0];
        const foundDateOfEvent = claim.damageDate;

        if (foundLatitude && foundLongitude && foundDateOfEvent) {
          setDopplerRequestDetails(
            await computeDopplerFrequencyAndData(foundLatitude, foundLongitude, foundDateOfEvent),
          );
        }

        // setLatitude(45.0703);
        // setLongitude(7.6869);
        setLatitude(foundLatitude);
        setLongitude(foundLongitude);
        setDateOfEvent(foundDateOfEvent);
        setIsPolicyFetching(false);
        const otherClaims = await httpClient().get(`/agricultureClaims/${policyNumber}/${complainId}/claims`);

        setPolicyClaims(otherClaims.data);
      } catch (e) {
        console.log(e);
        apiError(e);
      }
    };

    if (policyNumber && complainId) {
      getClaimData();
    }
  }, [policyNumber, complainId]);

  useEffect(() => {
    const loadDopplerData = async () => {
      const { latitude, longitude, dateAsYYYYMMDD, foundDopplerFrequency } = dopplerRequestDetails;
      setDopplerData({ max_vmi: "Computing..." });
      await httpClient()
        .post("/proxy/doppler", {
          lat: latitude,
          lng: longitude,
          frequency: foundDopplerFrequency,
          date: dateAsYYYYMMDD,
        })
        .then((resp) => resp.data)
        .then((data) => {
          console.log("computeDopplerFrequencyAndData:setDopplerData", data);
          setDopplerData(data);
        })
        .catch((err) => {
          console.log(err.message + " because no doppler data available for " + dateOfEvent);
        });
    };

    if (dopplerRequestDetails) {
      if (dopplerRequestDetails.foundDopplerFrequency > 0) {
        loadDopplerData();
      } else {
        console.log("computeDopplerFrequencyAndData:no doppler data available for " + dateOfEvent);
      }
    }
  }, [dopplerRequestDetails, dateOfEvent]);

  return policy ? (
    <Grid container style={{ marginTop: 15 }}>
      <Grid item xs={12} sm={4}>
        {!isPolicyFetching ? (
          <Card>
            <CardHeader style={{ paddingBottom: "0" }} title={t("claims.description")} />

            <CardContent style={{ paddingTop: "0" }}>
              <AgriculturePolicyDescription policy={policy} />
              <Divider />
              <AgricultureClaimDescription claim={claim} />
            </CardContent>
          </Card>
        ) : (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} sm={8}>
        <Box p={1}>
          <Card>
            {showVmiDbzMap && latitude && longitude && (
              <Box width="100%" height="620px">
                <DopplerVmiDbzMap
                  dateOfEvent={dateOfEvent}
                  latitude={latitude}
                  longitude={longitude}
                  dopplerFrequency={dopplerRequestDetails?.foundDopplerFrequency} // 0,12,48
                />
              </Box>
            )}
            {showDbzMap && latitude && longitude && (
              <Box width="100%" height="620px">
                <DopplerDbzMap
                  dateOfEvent={dateOfEvent}
                  latitude={latitude}
                  longitude={longitude}
                  dopplerFrequency={dopplerRequestDetails?.foundDopplerFrequency} // 0,12,48
                />
              </Box>
            )}
            {!showVmiDbzMap && !showDbzMap && latitude && longitude && (
              <HazardProvider>
                <AgriculturePolicyDetailsMap
                  longitude={longitude}
                  latitude={latitude}
                  policyNumber={policyNumber}
                  currentlyVisiblePartitas={currentlyVisiblePartitas}
                  complainId={complainId}
                />
              </HazardProvider>
            )}
          </Card>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Box mt={1}>
          {claim && dateOfEvent && latitude && longitude && (
            <EnvironmentCondition
              latitude={latitude}
              longitude={longitude}
              eventDate={dateOfEvent}
              claim={claim}
              updateShowDbz={updateShowDbz}
              updateShowVmiDbz={updateShowVmiDbz}
              dopplerData={dopplerData}
              dopplerFrequency={dopplerRequestDetails?.foundDopplerFrequency} // 0,12,48
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Box mt={1}>
          <ParcelsList
            policyNumber={policy.policyNumber}
            setCurrentlyVisiblePartitas={setCurrentlyVisiblePartitas}
            currentlyVisiblePartitas={currentlyVisiblePartitas}
          />
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Box mt={1}>
          {Object.keys(claim).length > 0 && (
            <AgriculturePolicyClaimsList claims={policyClaims}></AgriculturePolicyClaimsList>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <NdviByParcel policyNumber={policyNumber} complainId={complainId}></NdviByParcel>
      </Grid>
    </Grid>
  ) : (
    <></>
  );
};
